import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";
import { suggestClients } from "../../lib/clients";

/**
 * Input de telefon cu autocomplete intern.
 * Caută după cifrele tastate (min 3); serverul întoarce doar clienții cu telefon potrivit.
 *
 * onExactMatch(client): chemat când numărul tastat aparține unui singur client.
 * Întoarce true dacă l-a folosit; altfel rămâne lista de sugestii.
 */
const PhoneAutocomplete = React.forwardRef(({
  value = "",
  onChange,
  onPickClient,
  onExactMatch,
  label,
  labelClassName,
  placeholder = "07xx xxx xxx",
  error,
  required = false,
  disabled = false,
  id,
}, ref) => {
  const { t } = useTranslation();
  const inputId = id || `phone-${Math.random().toString(36).substring(2, 9)}`;
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  // Căutăm doar când numărul vine de la utilizator, nu când îl completează formularul
  // (client ales, deschiderea unei programări existente).
  const typedRef = useRef(false);
  const onExactMatchRef = useRef(onExactMatch);
  onExactMatchRef.current = onExactMatch;

  useEffect(() => {
    const typed = typedRef.current;
    typedRef.current = false;
    setLookupFailed(false);

    const digits = String(value || "").replace(/\D/g, "");
    if (!typed || digits.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    let stale = false;
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await suggestClients(digits, { limit: 8, field: "phone" });
        if (stale) return;

        const exact = rows.filter((c) => c.phone_exact);
        if (exact.length === 1 && onExactMatchRef.current?.(exact[0])) {
          setSuggestions([]);
          setIsOpen(false);
          return;
        }

        setSuggestions(rows);
        if (rows.length > 0) setIsOpen(true);
      } catch {
        if (stale) return;
        setSuggestions([]);
        setLookupFailed(true);
      }
    }, 250);

    return () => {
      stale = true;
      clearTimeout(debounceRef.current);
    };
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [isOpen]);

  return (
    <div className="space-y-2 relative" ref={wrapRef}>
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            "text-sm font-medium leading-none",
            error ? "text-destructive" : "text-foreground",
            labelClassName
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <input
        ref={ref}
        id={inputId}
        type="tel"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-destructive focus-visible:ring-destructive"
        )}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          typedRef.current = true;
          onChange?.(e);
        }}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        required={required}
        disabled={disabled}
      />

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-[300] top-full mt-1 w-full rounded-md border border-border bg-surface shadow-lg max-h-60 overflow-auto">
          {suggestions.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => {
                onPickClient?.(c);
                setSuggestions([]);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium">{c.name}</span>
                {c.is_vip && <span className="text-xs">⭐</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {c.phone || ""}
                {c.email ? ` • ${c.email}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}

      {lookupFailed && <p className="text-xs text-muted-foreground">{t("common.phoneLookupFailed")}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
});

PhoneAutocomplete.displayName = "PhoneAutocomplete";
export default PhoneAutocomplete;
